import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "@helpers/translate";
import useMediaQuery from "@mui/material/useMediaQuery";
import FuturesGuideTooltip from "../FuturesGuideTooltip";

import "./index.sass";

const TOOLTIP_GAP = 14;
const SPOTLIGHT_PADDING = 8;
const TOOLTIP_WIDTH = 352;
const VIEWPORT_PADDING = 16;
const MOBILE_MEDIA_QUERY = "(max-width: 600px)";
const RECT_COMPARE_EPSILON = 0.5;

const clamp = (value, min, max) => {
  if (max < min) return value;

  return Math.min(Math.max(value, min), max);
};

const wait = (delay = 0) => {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
};

const waitForNextFrame = () => {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
};

const getViewportSize = () => {
  if (typeof window === "undefined") {
    return {
      width: 0,
      height: 0,
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
};

const getStepConfig = (step, isMobile) => {
  if (!step) return null;

  if (isMobile) {
    if (step.mobile === null) return null;

    return step.mobile || step.desktop || null;
  }

  if (step.desktop === null) return null;

  return step.desktop || step.mobile || null;
};

const getTargetSelectors = (target) => {
  if (!target) return [];

  return Array.isArray(target) ? target.filter(Boolean) : [target];
};

const isElementVisible = (element) => {
  if (!element || typeof window === "undefined") return false;

  const styles = window.getComputedStyle(element);
  const rect = element.getBoundingClientRect();

  return (
    styles.display !== "none" &&
    styles.visibility !== "hidden" &&
    rect.width > 0 &&
    rect.height > 0
  );
};

const getVisibleElementsBySelector = (selector) => {
  if (!selector || typeof document === "undefined") return [];

  try {
    return Array.from(document.querySelectorAll(selector)).filter(
      isElementVisible,
    );
  } catch (error) {
    console.warn(`Futures onboarding invalid selector: ${selector}`, error);
    return [];
  }
};

const getVisibleElement = (selector) => {
  return getVisibleElementsBySelector(selector)[0] || null;
};

const getVisibleElements = (target) => {
  const selectors = getTargetSelectors(target);

  return selectors.flatMap((selector) =>
    getVisibleElementsBySelector(selector),
  );
};

const hasVisibleElementsForEachTarget = (target) => {
  const selectors = getTargetSelectors(target);

  if (!selectors.length) return false;

  return selectors.every((selector) => {
    return getVisibleElementsBySelector(selector).length > 0;
  });
};

const getCombinedRect = (rects) => {
  if (!rects.length) return null;

  const top = Math.min(...rects.map((rect) => rect.top));
  const left = Math.min(...rects.map((rect) => rect.left));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));

  return {
    top,
    left,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
};

const waitForElements = ({ target, timeout = 1200 }) => {
  return new Promise((resolve) => {
    const selectors = getTargetSelectors(target);

    if (!selectors.length || typeof document === "undefined") {
      resolve([]);
      return;
    }

    const startedAt = Date.now();

    const check = () => {
      const elements = getVisibleElements(target);

      if (hasVisibleElementsForEachTarget(target)) {
        resolve(elements);
        return;
      }

      if (Date.now() - startedAt >= timeout) {
        resolve([]);
        return;
      }

      requestAnimationFrame(check);
    };

    check();
  });
};

const runBeforeShowAction = async (beforeShow) => {
  if (!beforeShow || typeof document === "undefined") return;

  const actions = Array.isArray(beforeShow) ? beforeShow : [beforeShow];

  for (const action of actions) {
    if (action.type === "scrollToTop") {
      if (typeof window !== "undefined") {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: action.behavior || "smooth",
        });
      }

      if (action.delay) {
        await wait(action.delay);
      }

      continue;
    }

    if (action.type === "scrollIntoView") {
      const targetElement = getVisibleElement(action.target);

      if (!targetElement) {
        if (!action.optional) {
          console.warn(
            `Futures onboarding scroll target not found: ${action.target}`,
          );
        }

        continue;
      }

      targetElement.scrollIntoView({
        behavior: action.behavior || "smooth",
        block: action.block || "nearest",
        inline: action.inline || "center",
      });

      if (action.delay) {
        await wait(action.delay);
      }

      continue;
    }

    if (action.type !== "click") continue;

    if (action.waitFor && hasVisibleElementsForEachTarget(action.waitFor)) {
      continue;
    }

    const triggerElement = getVisibleElement(action.target);

    if (!triggerElement) {
      if (!action.optional) {
        console.warn(
          `Futures onboarding action target not found: ${action.target}`,
        );
      }

      continue;
    }

    triggerElement.click();

    if (action.waitFor) {
      await waitForElements({
        target: action.waitFor,
        timeout: action.timeout || 1200,
      });

      if (action.afterWaitDelay) {
        await wait(action.afterWaitDelay);
      }

      continue;
    }

    if (action.delay) {
      await wait(action.delay);
    }
  }
};

const scrollTargetToMobileTop = (targetElement, scrollConfig) => {
  if (!targetElement || typeof window === "undefined") return;

  const config =
    scrollConfig && typeof scrollConfig === "object" ? scrollConfig : {};

  const targetRect = targetElement.getBoundingClientRect();
  const documentHeight = document.documentElement.scrollHeight;
  const maxScrollTop = Math.max(documentHeight - window.innerHeight, 0);

  const targetViewportTop =
    typeof config.viewportPercent === "number"
      ? window.innerHeight * (config.viewportPercent / 100)
      : config.offsetTop ?? VIEWPORT_PADDING;

  const scrollTop = clamp(
    window.scrollY + targetRect.top - targetViewportTop,
    0,
    maxScrollTop,
  );

  window.scrollTo({
    top: scrollTop,
    behavior: config.behavior || "smooth",
  });
};

const scrollPageToTop = () => {
  if (typeof window === "undefined") return;

  setTimeout(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, 100);
};

const getSpotlightRect = (targetRect, viewportSize) => {
  const left = clamp(
    targetRect.left - SPOTLIGHT_PADDING,
    0,
    viewportSize.width,
  );

  const top = clamp(targetRect.top - SPOTLIGHT_PADDING, 0, viewportSize.height);

  const right = clamp(
    targetRect.right + SPOTLIGHT_PADDING,
    0,
    viewportSize.width,
  );

  const bottom = clamp(
    targetRect.bottom + SPOTLIGHT_PADDING,
    0,
    viewportSize.height,
  );

  return {
    top,
    left,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
};

const getBlurSections = ({ spotlightRects, viewportSize }) => {
  if (!spotlightRects.length) return [];

  const normalizePoint = (value, max) => {
    return clamp(Math.round(value * 100) / 100, 0, max);
  };

  const xPoints = [0, viewportSize.width];
  const yPoints = [0, viewportSize.height];

  spotlightRects.forEach((rect) => {
    xPoints.push(rect.left, rect.right);
    yPoints.push(rect.top, rect.bottom);
  });

  const xValues = Array.from(
    new Set(xPoints.map((value) => normalizePoint(value, viewportSize.width))),
  ).sort((a, b) => a - b);

  const yValues = Array.from(
    new Set(yPoints.map((value) => normalizePoint(value, viewportSize.height))),
  ).sort((a, b) => a - b);

  const sections = [];

  for (let yIndex = 0; yIndex < yValues.length - 1; yIndex += 1) {
    for (let xIndex = 0; xIndex < xValues.length - 1; xIndex += 1) {
      const left = xValues[xIndex];
      const right = xValues[xIndex + 1];
      const top = yValues[yIndex];
      const bottom = yValues[yIndex + 1];

      if (right <= left || bottom <= top) continue;

      const isInsideSpotlight = spotlightRects.some((rect) => {
        return (
          left >= rect.left - RECT_COMPARE_EPSILON &&
          right <= rect.right + RECT_COMPARE_EPSILON &&
          top >= rect.top - RECT_COMPARE_EPSILON &&
          bottom <= rect.bottom + RECT_COMPARE_EPSILON
        );
      });

      if (!isInsideSpotlight) {
        sections.push({
          top,
          left,
          width: right - left,
          height: bottom - top,
        });
      }
    }
  }

  return sections;
};

const getTooltipPosition = ({
  spotlightRect,
  placement,
  viewportSize,
  isMobile,
  offsetX = 0,
  offsetY = 0,
}) => {
  const centerX = spotlightRect.left + spotlightRect.width / 2;
  const centerY = spotlightRect.top + spotlightRect.height / 2;
  const tooltipWidth = isMobile
    ? viewportSize.width - VIEWPORT_PADDING * 2
    : TOOLTIP_WIDTH;

  const safeCenterX = clamp(
    centerX,
    VIEWPORT_PADDING + tooltipWidth / 2,
    viewportSize.width - VIEWPORT_PADDING - tooltipWidth / 2,
  );

  switch (placement) {
    case "top":
      return {
        top: spotlightRect.top - TOOLTIP_GAP + offsetY,
        left: safeCenterX + offsetX,
        transform: "translate(-50%, -100%)",
      };

    case "bottom":
      return {
        top: spotlightRect.bottom + TOOLTIP_GAP + offsetY,
        left: safeCenterX + offsetX,
        transform: "translateX(-50%)",
      };

    case "left":
      return {
        top: centerY + offsetY,
        left: spotlightRect.left - TOOLTIP_GAP + offsetX,
        transform: "translate(-100%, -50%)",
      };

    case "right":
      return {
        top: centerY + offsetY,
        left: spotlightRect.right + TOOLTIP_GAP + offsetX,
        transform: "translateY(-50%)",
      };

    default:
      return {
        top: spotlightRect.bottom + TOOLTIP_GAP + offsetY,
        left: safeCenterX + offsetX,
        transform: "translateX(-50%)",
      };
  }
};

const getAvailableStepIndex = ({ steps, startIndex, direction, isMobile }) => {
  if (typeof document === "undefined") return -1;

  let nextIndex = startIndex;

  while (nextIndex >= 0 && nextIndex < steps.length) {
    const step = steps[nextIndex];
    const config = getStepConfig(step, isMobile);

    if (!config) {
      nextIndex += direction;
      continue;
    }

    if (config.beforeShow) {
      return nextIndex;
    }

    if (config.target) {
      if (hasVisibleElementsForEachTarget(config.target)) {
        return nextIndex;
      }

      console.warn(`Futures onboarding target not found: ${config.target}`);
    }

    nextIndex += direction;
  }

  return -1;
};

const getCountableSteps = (steps, isMobile) => {
  return steps.filter((step) => {
    const config = getStepConfig(step, isMobile);

    return config && step.countAsStep !== false;
  });
};

const getCurrentStepNumber = ({ steps, currentStepIndex, isMobile }) => {
  const stepNumber = steps.slice(0, currentStepIndex + 1).filter((step) => {
    const config = getStepConfig(step, isMobile);

    return config && step.countAsStep !== false;
  }).length;

  return Math.max(stepNumber, 1);
};

const FuturesOnboardingGuide = ({ isOpen, steps = [], onClose }) => {
  const { __ } = useTranslation();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRects, setTargetRects] = useState([]);
  const [tooltipTargetRect, setTooltipTargetRect] = useState(null);
  const [viewportSize, setViewportSize] = useState(getViewportSize);

  const currentStep = steps[currentStepIndex];

  const stepConfig = useMemo(() => {
    return getStepConfig(currentStep, isMobile);
  }, [currentStep, isMobile]);

  const totalSteps = useMemo(() => {
    return getCountableSteps(steps, isMobile).length;
  }, [steps, isMobile]);

  const currentStepNumber = useMemo(() => {
    return getCurrentStepNumber({
      steps,
      currentStepIndex,
      isMobile,
    });
  }, [steps, currentStepIndex, isMobile]);

  const handleClose = useCallback(() => {
    onClose?.();
    setCurrentStepIndex(0);
    setTargetRects([]);
    setTooltipTargetRect(null);
  }, [onClose]);

  const goToAvailableStep = useCallback(
    (direction) => {
      const availableStepIndex = getAvailableStepIndex({
        steps,
        startIndex: currentStepIndex + direction,
        direction,
        isMobile,
      });

      if (availableStepIndex === -1) {
        handleClose();
        return;
      }

      setTargetRects([]);
      setTooltipTargetRect(null);
      setCurrentStepIndex(availableStepIndex);
    },
    [steps, currentStepIndex, isMobile, handleClose],
  );

  const updateTargetPosition = useCallback(() => {
    if (!stepConfig?.target) return;

    const targetElements = getVisibleElements(stepConfig.target);
    const nextViewportSize = getViewportSize();

    setViewportSize(nextViewportSize);

    if (!hasVisibleElementsForEachTarget(stepConfig.target)) {
      console.warn(`Futures onboarding target not found: ${stepConfig.target}`);
      setTargetRects([]);
      setTooltipTargetRect(null);
      goToAvailableStep(1);
      return;
    }

    const nextTargetRects = targetElements.map((element) =>
      element.getBoundingClientRect(),
    );

    setTargetRects(nextTargetRects);

    const tooltipTarget = stepConfig.tooltipTarget || stepConfig.target;
    const tooltipTargetElements = getVisibleElements(tooltipTarget);

    if (tooltipTargetElements.length) {
      setTooltipTargetRect(
        getCombinedRect(
          tooltipTargetElements.map((element) =>
            element.getBoundingClientRect(),
          ),
        ),
      );

      return;
    }

    setTooltipTargetRect(getCombinedRect(nextTargetRects));
  }, [stepConfig, goToAvailableStep]);

  useEffect(() => {
    if (!isOpen || stepConfig) return;

    goToAvailableStep(1);
  }, [isOpen, stepConfig, goToAvailableStep]);

  useEffect(() => {
    if (!isOpen || !stepConfig?.target) return;

    let isCancelled = false;
    let timeoutId = null;

    const prepareStep = async () => {
      await runBeforeShowAction(stepConfig.beforeShow);

      if (isCancelled) return;

      const targetElements = await waitForElements({
        target: stepConfig.target,
        timeout: stepConfig.timeout || 1200,
      });

      if (isCancelled) return;

      if (!targetElements.length) {
        console.warn(
          `Futures onboarding target not found: ${stepConfig.target}`,
        );
        setTargetRects([]);
        setTooltipTargetRect(null);
        goToAvailableStep(1);
        return;
      }

      if (stepConfig.scroll !== false) {
        if (isMobile) {
          scrollTargetToMobileTop(targetElements[0], stepConfig.scroll);
        } else {
          const scrollConfig =
            stepConfig.scroll && typeof stepConfig.scroll === "object"
              ? stepConfig.scroll
              : {};

          targetElements[0].scrollIntoView({
            behavior: scrollConfig.behavior || "smooth",
            block: scrollConfig.block || "center",
            inline: scrollConfig.inline || "center",
          });
        }
      }

      const positionDelay = stepConfig.readyDelay ?? 0;

      if (positionDelay > 0) {
        timeoutId = setTimeout(() => {
          if (!isCancelled) {
            updateTargetPosition();
          }
        }, positionDelay);

        return;
      }

      await waitForNextFrame();

      if (!isCancelled) {
        updateTargetPosition();
      }
    };

    prepareStep();

    return () => {
      isCancelled = true;

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isOpen, stepConfig, isMobile, updateTargetPosition, goToAvailableStep]);

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener("resize", updateTargetPosition);
    window.addEventListener("scroll", updateTargetPosition, true);

    return () => {
      window.removeEventListener("resize", updateTargetPosition);
      window.removeEventListener("scroll", updateTargetPosition, true);
    };
  }, [isOpen, updateTargetPosition]);

  useEffect(() => {
    if (!isOpen) return;

    document.body.classList.add("futures-guide-open");

    return () => {
      document.body.classList.remove("futures-guide-open");
    };
  }, [isOpen]);

  const spotlightRects = useMemo(() => {
    if (!targetRects.length) return [];

    return targetRects.map((rect) => getSpotlightRect(rect, viewportSize));
  }, [targetRects, viewportSize]);

  const combinedSpotlightRect = useMemo(() => {
    if (!spotlightRects.length) return null;

    return getCombinedRect(spotlightRects);
  }, [spotlightRects]);

  const tooltipPositionRect = useMemo(() => {
    if (!tooltipTargetRect) return combinedSpotlightRect;

    return getSpotlightRect(tooltipTargetRect, viewportSize);
  }, [tooltipTargetRect, combinedSpotlightRect, viewportSize]);

  const blurSections = useMemo(() => {
    if (!spotlightRects.length) return [];

    return getBlurSections({
      spotlightRects,
      viewportSize,
    });
  }, [spotlightRects, viewportSize]);

  const tooltipPosition = useMemo(() => {
    if (!tooltipPositionRect || !stepConfig) return null;

    return getTooltipPosition({
      spotlightRect: tooltipPositionRect,
      placement: stepConfig.placement,
      viewportSize,
      isMobile,
      offsetX: stepConfig.offsetX,
      offsetY: stepConfig.offsetY,
    });
  }, [tooltipPositionRect, stepConfig, viewportSize, isMobile]);

  const handleBack = () => {
    if (currentStepIndex === 0) return;

    goToAvailableStep(-1);
  };

  const handleNext = () => {
    if (currentStepIndex >= steps.length - 1 || currentStep?.isLastStep) {
      handleClose();
      scrollPageToTop();
      return;
    }

    goToAvailableStep(1);
  };

  const preventSpotlightInteraction = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  if (!isOpen || !currentStep || !stepConfig) return null;

  return createPortal(
    <div className="futures-guide">
      {blurSections.map((section, index) => (
        <div
          key={index}
          className="futures-guide__blur futures-guide__blur--top"
          style={section}
        />
      ))}

      {spotlightRects.map((rect, index) => (
        <div
          key={`spotlight-blocker-${index}`}
          className="futures-guide__spotlight-blocker"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
          onPointerDown={preventSpotlightInteraction}
          onPointerUp={preventSpotlightInteraction}
          onClick={preventSpotlightInteraction}
          onDoubleClick={preventSpotlightInteraction}
          onContextMenu={preventSpotlightInteraction}
        />
      ))}

      {tooltipPosition && (
        <FuturesGuideTooltip
          title={__(currentStep.title)}
          description={__(currentStep.description)}
          currentStep={currentStepNumber}
          totalSteps={totalSteps}
          placement={stepConfig.placement}
          isFirstStep={currentStepNumber === 1}
          isLastStep={
            currentStepIndex === steps.length - 1 || currentStep?.isLastStep
          }
          style={tooltipPosition}
          arrow={stepConfig.arrow}
          onBack={handleBack}
          onNext={handleNext}
        />
      )}
    </div>,
    document.body,
  );
};

export default FuturesOnboardingGuide;
